import arcgis = require("arcgis/services/featureserver/layer");
import Query = require("arcgis/services/featureserver/query");
import Geometry = require("esri/geometry/Geometry");
import Point = require("esri/geometry/Point");
import Polyline = require("esri/geometry/Polyline");
import Polygon = require("esri/geometry/Polygon");
import Multipoint = require("esri/geometry/Multipoint");
import Portal = require("portal-gis");
import Templates = require("../../models/defaults");
import Utils = require("../../utils");
import geo = require("../geo");
import StyleConverter = require("./style-converter");

const dataTypeMapping = {
    esriFieldTypeString: "string",
    esriFieldTypeSmallInteger: "number",
    esriFieldTypeInteger: "number",
    esriFieldTypeDouble: "number",
    esriFieldTypeSingle: "number",
    esriFieldTypeDate: "date",
    // special enumerations
    esriFieldTypeOID: "number",
    esriFieldTypeGlobalID: "number",
    esriFieldTypeGUID: "string",
    esriFieldTypeXML: "string",
    esriFieldTypeGeometry: "geometry"
};

const geometryTypeMapping = {
    esriGeometryPoint: "point",
    esriGeometryPolyline: "multiline",
    esriGeometryPolygon: "polygon",
    esriGeometryMultipoint: "multipoint"
};

class Converter {

    static about = `Converts native ArcGIS Server features to configurations.`;

    private forceTransformer() {
        let t = geo.transformer(this.sourceSrs, this.targetSrs);
        this.forceTransformer = () => t;
        return t;
    }

    private asSymbology(drawingInfo: arcgis.DrawingInfo, args: { url: string }) {
        let converter = new StyleConverter();
        return converter.asSymbology(drawingInfo, args);
    }

    constructor(public sourceSrs = "EPSG:4326", public targetSrs = "EPSG:4326") {
    }

    private fromNativeGeom = {
        point: (g: Point) => this.forceTransformer().point([g.x, g.y]),
        multiline: (g: Polyline) => this.forceTransformer().paths(g.paths),
        polygon: (g: Polygon) => this.forceTransformer().paths(g.rings),
        multipoint: (g: Multipoint) => this.forceTransformer().points(g.points),
        unknown: (g: any) => {
            return null;
        }
    }

    private fromNativeFeature(f, geometryType?: string) {
        geometryType = geometryType || geometryTypeMapping[f.geometry.type] || "unknown";
        return {
            geometry: this.fromNativeGeom[geometryType](f.geometry),
            geometryType: geometryType,
            attributes: f.attributes
        };
    }

    public fromNativeFeatures(agsFeatures: Array<Query.Feature>) {
        let features = agsFeatures.map(f => <Rhythm.GIS.FeatureQueryResultFeature>this.fromNativeFeature(f, f["geometryType"] && geometryTypeMapping[f["geometryType"]]));
        return features;
    }

    /* converts arg data to an internal format */
    public fromNativeLayer(layer: arcgis.RootObject, args: { url: string }) {

        let result = <Rhythm.GIS.FeatureQueryResultLayer>{};

        result.spatialReference = {
            wkid: layer.extent.spatialReference.wkid
        };

        result.displayFieldName = layer["displayField"];

        result.geometryType = geometryTypeMapping[layer.geometryType];

        result.fields = layer.fields.map(f => ({
            name: f.name,
            type: dataTypeMapping[f.type] || null,
            alias: f.alias,
            domain: f.domain,
            length: f.length
        }));

        result.symbols = args && this.asSymbology(layer.drawingInfo, args);

        return result;
    }

    /* converts arg data to an internal format */
    public fromNativeQuery(layer: arcgis.RootObject, args?: any) {

        let result = <Rhythm.GIS.FeatureQueryResultLayer>{};

        result.spatialReference = { wkid: layer.spatialReference.wkid };

        result.displayFieldName = layer.displayFieldName;

        result.geometryType = geometryTypeMapping[layer.geometryType];

        result.features = layer["features"].map(f => this.fromNativeFeature(f, result.geometryType));
        if (!result.geometryType) {
            result.geometryType = result.features[0] && result.features[0].geometryType;
        }

        result.fields = layer.fields.map(f => ({
            name: f.name,
            type: dataTypeMapping[f.type] || null,
            alias: f.alias,
            length: f.length
        }));

        return result;
    }
}

export = Converter;
